import { AutocompletePredictionPredictionCategory, AutocompletePredictionTypes, QueryResultType } from './types';

/**
 * Classifies a Google Places Autocomplete prediction based on its types.
 *
 * The function examines the array of `types` provided in the prediction result and classifies the prediction
 * into one of the following categories:
 *
 * - `'street'`: If the prediction includes `street_address`, `premise`, or `route`, which represent streets, specific buildings, or parts of buildings.
 * - `'city'`: If the prediction includes `locality`, which represents a city or town.
 * - `'neighborhood'`: If the prediction includes `sublocality` or `neighborhood`, which represent neighborhoods or districts within cities.
 * - `'province'`: If the prediction includes `administrative_area_level_1`, a province or state.
 * - `'postal_code'`: If the prediction includes `postal_code`.
 * - `'other'`: If none of the above categories match.
 *
 * Residential search does not show Google provinces or states; those come from go-search.
 * eXp Commercial search shows Google states and ZIP codes.
 *
 * @param types - An array of type strings from the Google Places Autocomplete prediction.
 *                These types indicate the nature of the prediction (e.g., address, locality, establishment).
 *
 * @returns A `PredictionCategory` indicating whether the prediction is a city, neighborhood, street, or other.
 *
 * @see {@link https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Types Google Maps Geocoding API Types}
 *
 */
export const getPredictionCategory = (types: string[]): AutocompletePredictionPredictionCategory => {
  switch (true) {
  // Street-level predictions
    case types.includes('street_address') || types.includes('premise') || types.includes('route'):
      return AutocompletePredictionPredictionCategory.STREET;

      // City predictions
    case types.includes('locality'):
      return AutocompletePredictionPredictionCategory.CITY;

      // Neighborhood predictions
    case types.includes('sublocality') || types.includes('neighborhood'):
      return AutocompletePredictionPredictionCategory.NEIGHBORHOOD;

    case types.includes('administrative_area_level_1'):
      return AutocompletePredictionPredictionCategory.PROVINCE;
    case types.includes('postal_code'):
      return AutocompletePredictionPredictionCategory.POSTAL_CODE;
    default:
      return AutocompletePredictionPredictionCategory.OTHER;
  }
};

// Google lists the most specific type first (Autocomplete samples use
// ["locality", "political", "geocode"], ["street_address", "geocode"],
// ["postal_code", "geocode"]). Matching only types[0] keeps establishments out.
// `geocode` is on almost every prediction, so types.some(...) would not filter.
const SUPPORTED_PLACE_TYPES: string[] = [
  AutocompletePredictionTypes.NEIGHBORHOOD,
  AutocompletePredictionTypes.GEOCODE,
  AutocompletePredictionTypes.LOCALITY,
  AutocompletePredictionTypes.SUBLOCALITY,
  AutocompletePredictionTypes.SUBLOCALITY_LEVEL_1,
  AutocompletePredictionTypes.ADMINISTRATIVE_AREA_LEVEL_1,
  AutocompletePredictionTypes.ADMINISTRATIVE_AREA_LEVEL_2,
  AutocompletePredictionTypes.ADMINISTRATIVE_AREA_LEVEL_3,
  AutocompletePredictionTypes.ROUTE,
  AutocompletePredictionTypes.STREET_ADDRESS,
  AutocompletePredictionTypes.PREMISE,
  AutocompletePredictionTypes.POSTAL_CODE,
  AutocompletePredictionTypes.POINT_OF_INTEREST,
];

const PLACE_TYPE_INDEX = 0;

// Drops unsupported and duplicate places, then groups the rest by category.
export function categorizePredictions(predictions: readonly google.maps.places.AutocompletePrediction[]): QueryResultType {
  const result: QueryResultType = {
    city: [],
    neighborhood: [],
    street: [],
    // right now expCommercial uses province & postal code predictions from google maps
    province: [],
    postalCode: [],
  };
  const seenPlaceIds = new Set<string>();

  for (const prediction of predictions) {
    if (!SUPPORTED_PLACE_TYPES.includes(prediction.types[PLACE_TYPE_INDEX]) || seenPlaceIds.has(prediction.place_id)) {
      continue;
    }
    seenPlaceIds.add(prediction.place_id);

    switch (getPredictionCategory(prediction.types)) {
      case AutocompletePredictionPredictionCategory.CITY:
        result.city.push(prediction);
        break;
      case AutocompletePredictionPredictionCategory.NEIGHBORHOOD:
        result.neighborhood.push(prediction);
        break;
      case AutocompletePredictionPredictionCategory.STREET:
        result.street.push(prediction);
        break;
      case AutocompletePredictionPredictionCategory.PROVINCE:
        result.province.push(prediction);
        break;
      case AutocompletePredictionPredictionCategory.POSTAL_CODE:
        result.postalCode.push(prediction);
        break;
      default:
        // Ignore 'other' predictions for now
        break;
    }
  }

  return result;
}
